import {useAssessmentFormStorage} from '@/util/hooks/useAssessmentFormStorage';
import {useStableCallback} from '@/util/hooks/useStableCallback';
import {useMemo, useState} from 'react';
import {AssessmentSection, ExpertiseWithGroup, Question} from '@/types/assessment';

type Section = {
    id: string;
    title: string;
    questions: Question[];
};

export const useAssessment = ({storageKey, onChange, formSections, tabsCount}: {
    storageKey?: string;
    onChange?: (data: any) => void;
    formSections: AssessmentSection[];
    tabsCount: number;
}) => {
    const [answers, setAnswers, clearAnswers] = useAssessmentFormStorage(storageKey);
    const [currentTab, setCurrentTab] = useState('0');

    const handleChange = useStableCallback(({id, score, checked, expertise_id}: {
        id: string;
        score: number;
        checked: boolean;
        expertise_id: string;
    }) => {
        const updatedAnswers = {
            ...answers,
            [id]: checked ? {assessment_question_id: id, score: Number(score), expertise_id: expertise_id} : null
        };
        const formData = Object.values(updatedAnswers).filter(Boolean);
        if (typeof onChange === 'function') {
            onChange({
                formData
            })
        }
        setAnswers(updatedAnswers)
    });

    const formData = useMemo(() => {
        return Object.values(answers).filter(Boolean);
    }, [answers])

    const isValidForm = useMemo(() => {
        const allQuestionIds = formSections.flatMap(it => it.questions || []).map(it => it.id);
        return allQuestionIds.every(id => {
            return answers[id];
        })
    }, [answers, formSections]);

    const byExpertiseGroup: {
        [key: string]: Section
    } = formSections.reduce((acc: {
        [key: string]: Section
    }, {
            expertise,
            questions
    }: {
        expertise: ExpertiseWithGroup;
        questions: Question[];
    }) => {
        const key = expertise?.expertise_group?.id;

        acc[key] = acc[key] || {
            ...expertise.expertise_group,
        };

        acc[key].questions = acc[key].questions || [];
        acc[key].questions.push(...questions);

        return acc;
    }, {});
    const sectionsByExpertise = Object.values(byExpertiseGroup);
    const hasNextSection = Number(currentTab) < tabsCount - 1;
    const hasPreviousSection = Number(currentTab) > 0;

    const sectionsProgress = sectionsByExpertise.map((it, index) => {
        const answeredQuestionsCount = it.questions.filter(it => {
            return answers[it.id];
        }).length;

        const sectionCompleted = answeredQuestionsCount === it.questions.length;

        return {
            completed: sectionCompleted,
        }
    });

    return {
        answers,
        isValidForm,
        clearAnswers,
        hasNextSection,
        hasPreviousSection,
        setCurrentTab,
        currentTab,
        sectionsByExpertise,
        formData: formData,
        onChange: handleChange,
        progress: sectionsProgress,
    } as const;
}
